import L from 'leaflet';
import { useEffect, useRef } from 'react';
import LocationService from '../../Core/Services/LocationService';

export default function LocationMap({
  locations = [],
  selectedLocation = null,
}) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef({});

  const { defaultIcon, selectedIcon } = LocationService.getMarkerIcons();

  // Initialise map once on mount
  useEffect(() => {
    if (!mapRef.current) return;

    const { center, zoom } = LocationService.getMapConfig();

    const map = L.map(mapRef.current, {
      center,
      zoom,
    });

    // Use our WordPress tile proxy endpoint instead of direct OpenStreetMap access
    // This ensures proper User-Agent and Referer headers are sent to comply with OSM requirements
    L.tileLayer('/tcrs2-tiles/{z}/{x}/{y}.png', {
      attribution:
        '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      crossOrigin: 'anonymous',
    }).addTo(map);

    mapInstanceRef.current = map;

    return () => {
      map.remove();
      mapInstanceRef.current = null;
      markersRef.current = {};
    };
  }, []);

  // Add/update markers whenever locations change
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    // Clear existing markers
    Object.values(markersRef.current).forEach(marker => marker.remove());
    markersRef.current = {};

    locations.forEach(location => {
      if (!location.latitude || !location.longitude) return;

      const isSelected = selectedLocation?.id === location.id;
      const marker = L.marker([location.latitude, location.longitude], {
        icon: isSelected ? selectedIcon : defaultIcon,
      })
        .addTo(map)
        .bindPopup(LocationService.buildMarkerPopup(location));

      markersRef.current[location.id] = marker;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations]);

  // Highlight selected marker when selection changes
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    Object.entries(markersRef.current).forEach(([id, marker]) => {
      const isSelected = selectedLocation?.id === id;
      marker.setIcon(isSelected ? selectedIcon : defaultIcon);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation]);

  return (
    <div
      ref={mapRef}
      className="h-full m-0 p-0"
      style={{ height: '100%', width: '100%', minHeight: '200px' }}
    ></div>
  );
}
